/**
 * The Integration API contract (spec §9.2).
 *
 * This is the single most externally-visible artefact in the codebase: SageDocs and
 * every integrator after it code against exactly this shape. Two consequences:
 *
 *  1. It is versioned (`/api/v1`). Additive changes only; a field is never renamed or
 *     narrowed within a version.
 *  2. The SAME schema validates the inbound payload on the server and the outbound
 *     payload in any first-party client — one definition, no drift.
 */
import { z } from 'zod';
import { deliveryModeSchema, documentStatusSchema, messageTypeSchema, submissionTypeSchema, } from './enums.js';
import { abnSchema, countryCodeSchema, decimalStringSchema, emailSchema, hsCodeSchema, internationalAddressSchema, isoDateSchema, isoDateTimeSchema, phoneSchema, positiveDecimalStringSchema, slugSchema, } from './primitives.js';
/** One line of goods on the certificate. Becomes a row on the printed form. */
export const goodsLineSchema = z.object({
    lineNumber: z.number().int().positive().optional(),
    hsCode: hsCodeSchema,
    description: z.string().trim().min(1).max(2000),
    /** Marks and numbers on packages — box 7 on most FTA forms. Optional by design. */
    marksAndNumbers: z.string().trim().max(500).optional().nullable(),
    packageCount: z.number().int().nonnegative().optional().nullable(),
    packageKind: z.string().trim().max(50).optional().nullable(),
    quantity: positiveDecimalStringSchema,
    unit: z.string().trim().min(1).max(20),
    grossWeight: decimalStringSchema.optional().nullable(),
    netWeight: decimalStringSchema.optional().nullable(),
    weightUnit: z.string().trim().max(10).default('KG'),
    value: decimalStringSchema.optional().nullable(),
    currency: z.string().trim().length(3).toUpperCase().default('AUD'),
    countryOfOrigin: countryCodeSchema,
    /**
     * WO (wholly obtained), PE (produced entirely), CTC (change in tariff
     * classification), QVC (qualifying value content), PSR, SP, or an agreement-specific
     * value. Free text because each FTA names these differently and a closed enum here
     * would need a deploy every time a new agreement is signed.
     */
    originCriterion: z.string().trim().max(40).optional().nullable(),
    invoiceNumber: z.string().trim().max(100).optional().nullable(),
    invoiceDate: isoDateSchema.optional().nullable(),
});
export const partySchema = z.object({
    companyName: z.string().trim().min(1).max(200),
    abn: abnSchema.optional().nullable(),
    address: internationalAddressSchema,
    contactName: z.string().trim().max(120).optional().nullable(),
    contactEmail: emailSchema.optional().nullable(),
    contactPhone: phoneSchema.optional().nullable(),
});
export const shipmentSchema = z.object({
    finalDestinationCountry: countryCodeSchema,
    portOfLoading: z.string().trim().max(120).optional().nullable(),
    portOfDischarge: z.string().trim().max(120).optional().nullable(),
    vesselOrFlight: z.string().trim().max(120).optional().nullable(),
    departureDate: isoDateSchema.optional().nullable(),
    invoiceNumber: z.string().trim().max(100).optional().nullable(),
    invoiceDate: isoDateSchema.optional().nullable(),
    invoiceTotal: decimalStringSchema.optional().nullable(),
    currency: z.string().trim().length(3).toUpperCase().default('AUD'),
    remarks: z.string().trim().max(2000).optional().nullable(),
    goodsLines: z.array(goodsLineSchema).min(1, 'At least one goods line is required').max(200),
});
/**
 * 25MB matches MAX_UPLOAD_MB. Enforced here as a cheap pre-check on the decoded length
 * so an oversized payload is refused before it is written anywhere.
 */
const MAX_BASE64_BYTES = 25 * 1024 * 1024;
export const fileUploadSchema = z.object({
    filename: z.string().trim().min(1).max(255),
    contentBase64: z
        .string()
        .min(1)
        .refine((v) => (v.length * 3) / 4 <= MAX_BASE64_BYTES, {
        message: 'File exceeds the 25MB limit',
    }),
    contentType: z.string().trim().max(120).default('application/pdf'),
});
export const supportingDocumentSubmissionSchema = fileUploadSchema.extend({
    /** A master-data code (spec §13.3), e.g. COMMERCIAL_INVOICE. Unknown codes fall to OTHER. */
    category: z.string().trim().min(1).max(60),
    description: z.string().trim().max(500).optional().nullable(),
    /**
     * Asks the chamber to also sign/stamp this specific file at approval, using the
     * Supporting Document Stamp (spec §7.6.3) — deliberately a different seal from the
     * certificate's own, and placed by the operator rather than from a saved coordinate,
     * because exporter-supplied invoices have no predictable layout.
     */
    requestSignatureAndStamp: z.boolean().default(false),
});
export const submitCertificateRequestSchema = z
    .object({
    /** The tenancy routing key. One shared API serves many chambers (spec §9.1). */
    chamberId: slugSchema,
    /** A document type code the chamber is accredited for, e.g. CHAFTA_CO. */
    documentType: z.string().trim().min(1).max(60).toUpperCase(),
    submissionType: submissionTypeSchema.default('ORIGINAL'),
    /** Required when submissionType is AMENDMENT. Identifies the certificate amended. */
    originalCertificateNo: z.string().trim().max(60).optional().nullable(),
    /** The integrator's own reference. Echoed back on every response and webhook. */
    senderReference: z.string().trim().min(1).max(60),
    deliveryMode: deliveryModeSchema.default('ELECTRONIC'),
    /** Drives SLA ageing in the operator's queue. */
    requestedTargetDate: isoDateSchema.optional().nullable(),
    exporter: partySchema,
    consignee: partySchema.optional().nullable(),
    producer: partySchema.optional().nullable(),
    shipment: shipmentSchema,
    /** The prepared certificate PDF, drafted by the integrator. */
    documentFile: fileUploadSchema,
    supportingDocuments: z.array(supportingDocumentSubmissionSchema).max(20).default([]),
    /** Where status changes are POSTed. Overrides the integrator's configured endpoint. */
    callbackUrl: z.string().url().max(2000).optional().nullable(),
    /** Opaque integrator data, returned verbatim on every response. Never interpreted. */
    metadata: z.record(z.unknown()).optional().nullable(),
})
    .superRefine((value, ctx) => {
    if (value.submissionType === 'AMENDMENT' && !value.originalCertificateNo) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['originalCertificateNo'],
            message: 'An AMENDMENT must name the certificate it amends',
        });
    }
    // An amendment number derives from the original; drawing a fresh sequence value
    // would break the traceable link back to it (spec §9.5).
    if (value.submissionType === 'ORIGINAL' && value.originalCertificateNo) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['originalCertificateNo'],
            message: 'Only an AMENDMENT may name an original certificate',
        });
    }
});
export const submitCertificateResponseSchema = z.object({
    docId: z.string(),
    status: documentStatusSchema,
    certificateNo: z.string().nullable(),
    senderReference: z.string(),
    chamberId: z.string(),
    receivedAt: isoDateTimeSchema,
    /**
     * Advisory warnings about a submission that has ALREADY been accepted.
     *
     * An additive, optional field: integrators built before this existed keep working, and
     * one that ignores it loses nothing but the early notice. Nothing here is ever a reason
     * the submission failed — a failure is an HTTP error with a machine-readable `code`.
     */
    warnings: z
        .array(z.object({
        code: z.string(),
        message: z.string(),
        detail: z.record(z.unknown()).optional(),
    }))
        .default([]),
});
export const rejectionReasonRefSchema = z.object({
    code: z.string(),
    label: z.string(),
    category: z.string().nullable().optional(),
    freeText: z.string().nullable().optional(),
});
/** What a poll, an outstanding-fetch entry and a webhook body all resolve to. */
export const certificateRequestStatusSchema = z.object({
    docId: z.string(),
    chamberId: z.string(),
    senderReference: z.string(),
    documentType: z.string(),
    submissionType: submissionTypeSchema,
    messageType: messageTypeSchema,
    status: documentStatusSchema,
    certificateNo: z.string().nullable(),
    verificationCode: z.string().nullable().optional(),
    certificatePdfUrl: z.string().nullable().optional(),
    originalCertificateNo: z.string().nullable().optional(),
    rejectionReason: rejectionReasonRefSchema.nullable().optional(),
    clarification: z
        .object({
        items: z.array(z.object({ code: z.string(), label: z.string() })),
        freeText: z.string().nullable(),
        requestedAt: isoDateTimeSchema,
        respondBy: isoDateTimeSchema.nullable().optional(),
    })
        .nullable()
        .optional(),
    supportingDocuments: z
        .array(z.object({
        id: z.string(),
        category: z.string(),
        filename: z.string(),
        stampedPdfUrl: z.string().nullable(),
    }))
        .optional(),
    receivedAt: isoDateTimeSchema,
    processedAt: isoDateTimeSchema.nullable(),
    metadata: z.record(z.unknown()).nullable().optional(),
});
export const outstandingQuerySchema = z.object({
    chamberId: slugSchema.optional(),
    /** Comma-separated. Defaults to every decided status. */
    status: z.string().optional(),
    cursor: z.string().optional(),
    limit: z.coerce.number().int().min(1).max(200).default(50),
});
export const outstandingResponseSchema = z.object({
    results: z.array(certificateRequestStatusSchema),
    nextCursor: z.string().nullable(),
    /** How many decided-but-unacknowledged items remain beyond this page. */
    remaining: z.number().int().nonnegative(),
});
export const respondClarificationSchema = z.object({
    message: z.string().trim().max(4000).optional().nullable(),
    attachments: z.array(supportingDocumentSubmissionSchema).max(20).default([]),
    /** Replaces the certificate PDF outright when the integrator re-prepared it. */
    replacementDocumentFile: fileUploadSchema.optional().nullable(),
});
export const addAttachmentsSchema = z.object({
    attachments: z.array(supportingDocumentSubmissionSchema).min(1).max(20),
});
export const webhookEnvelopeSchema = z.object({
    id: z.string(),
    event: z.string(),
    createdAt: isoDateTimeSchema,
    data: certificateRequestStatusSchema,
});
//# sourceMappingURL=certificate-request.js.map